import type { Context } from "hono";
import z from "zod";
import normalize from "normalize-path";

const pathSchema = z
  .string()
  .transform((p) => {
    let decoded: string;
    try {
      decoded = decodeURIComponent(p);
    } catch {
      decoded = p;
    }

    if (decoded.startsWith("~") || decoded.includes("\0")) {
      throw new Error(
        "Invalid path: tilde expansion and null bytes not allowed",
      );
    }

    if (decoded.startsWith("/") || /^[a-zA-Z]:/u.test(decoded)) {
      throw new Error("Absolute paths are not allowed");
    }

    const stripped = decoded.replace(/^\/+/u, "");
    const normalized = normalize(stripped).replace(/\\/g, "/");

    return normalized;
  })
  .refine((p) => p.length > 0, {
    message: "File path cannot be empty",
  })
  .refine((p) => !p.split("/").some((segment) => segment === ".."), {
    message: "Path traversal is not allowed",
  });

export const updateProjectFileInputSchema = z.object({
  path: pathSchema,
  content: z.string(),
  createIfNotExists: z.boolean().optional(),
});

export const updateProjectFileRequestBodySchema = z.object({
  files: z
    .array(updateProjectFileInputSchema)
    .min(1, "At least one file must be provided"),
});

export type UpdateProjectFileRequestBody = z.infer<
  typeof updateProjectFileRequestBodySchema
>;

export type UpdateProjectFileInput = z.infer<
  typeof updateProjectFileInputSchema
>;

export type UpdateProjectFileChangedFile = {
  path: string;
  status: string;
};

export type UpdateProjectFileCommit = {
  sha: string;
  shortSha: string;
  message: string;
};

export type UpdateProjectFileResponseBody = {
  success: true;
  paths: string[];
  commit: UpdateProjectFileCommit | null;
  changedFiles: UpdateProjectFileChangedFile[];
  warnings?: string[];
};

export type UpdateProjectFileHandlerReturnType = Promise<
  ReturnType<
    typeof Context.prototype.json<
      | UpdateProjectFileResponseBody
      | { error: string }
      | { errors: z.ZodIssue[] }
    >
  >
>;
